// src/pages/Dashboard/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import DashboardSidebar from './components/DashboardSidebar';
import DashboardHeader from './components/DashboardHeader';
import NodeManagement from './sections/NodeManagement';
import InvitationManagement from './sections/InvitationManagement';
import NetworkManagement from './sections/NetworkManagement';
import DashboardOverview from './sections/DashboardOverview';
import WalletRequiredModal from '../../components/WalletRequiredModal';
import { formatWalletAddress, formatDate, formatNumber } from '../../utils/formatters';
import { getValidSignature, clearSignatureCache } from '../../services/signatureService';
import './styles/dashboard.css';

/**
 * Main Dashboard Component
 * Serves as the container for the dashboard workbench
 */
const Dashboard = () => {
  const { wallet, openWalletModal } = useWallet();
  const [activatedNodes, setActivatedNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);
  const [networkStats, setNetworkStats] = useState(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const location = useLocation();

  /**
   * Convert network name/string to numeric ID
   * This is crucial for API compatibility
   * @param {string|number} networkId - Network name or ID
   * @returns {number} - Numeric network ID
   */
  const convertNetworkToNumeric = (networkId) => {
    if (typeof networkId === 'number') return networkId;
    
    // If it's already a numeric string, convert it
    if (!isNaN(parseInt(networkId))) return parseInt(networkId);
    
    // Map network names to IDs
    const networkMap = {
      'ethereum': 1,
      'eth': 1,
      'bsc': 2,
      'binance': 2,
      'solana': 3,
      'sol': 3,
      'polygon': 4
    };
    
    // Convert to lowercase to ensure matching
    const networkName = String(networkId).toLowerCase();
    return networkMap[networkName] || 1; // Default to 1 (ethereum) if not found
  };

  // Check if user is authenticated and fetch their nodes
  useEffect(() => {
    // Reset state when wallet changes
    setActivatedNodes([]);
    setSelectedNode(null);
    setNetworkStats(null);
    
    if (!wallet.connected) {
      setIsLoading(false);
      setShowWalletModal(true);
      return;
    }
    
    setShowWalletModal(false);
    fetchUserNodes();
  }, [wallet.connected, wallet.address, wallet.network]);

  /**
   * Fetch user's nodes using the wallet_applications endpoint
   * Updated to use the new API documentation endpoints and handle signature properly
   */
  const fetchUserNodes = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Convert network ID to numeric format for API compatibility
      const networkId = convertNetworkToNumeric(wallet.networkId || wallet.network || 1);
      
      // Get a valid signature with proper error handling
      try {
        const { signature, message } = await getValidSignature(
          wallet.address,
          networkId,
          wallet.type || 'metamask',
          false // Don't force refresh initially
        );
        
        // Use the node-applications/wallet_applications/ endpoint to get nodes
        // This is the correct endpoint as per the updated API documentation
        const response = await fetch('https://api.aeronyx.network/api/node-applications/wallet_applications/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            wallet_address: wallet.address,
            blockchain_network_id: networkId, // Use numeric network ID
            signature: signature,
            message: message,
            wallet_type: wallet.type
          }),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch nodes: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (!result.success) {
          // Check if the error is related to signature
          if (result.message && (
            result.message.toLowerCase().includes('signature') || 
            result.message.toLowerCase().includes('auth') ||
            result.message.toLowerCase().includes('token'))
          ) {
            // Clear signature cache and retry with a fresh signature
            clearSignatureCache();
            throw new Error('Signature validation failed, retrying...');
          }
          throw new Error(result.message || 'Failed to fetch user nodes');
        }
        
        // Extract nodes from the data structure according to API documentation
        // The API returns applications in result.data.applications
        const nodes = result.data && result.data.applications ? result.data.applications : [];
        
        if (nodes.length > 0) {
          // Sort nodes by tier level (higher tiers first)
          const sortedNodes = [...nodes].sort((a, b) => {
            // Parse tier level or use default values
            const tierA = a.tier_level || getTierLevel(a.tier_name || a.tier);
            const tierB = b.tier_level || getTierLevel(b.tier_name || b.tier);
            return tierB - tierA;
          });
          
          setActivatedNodes(sortedNodes);
          
          // Select the highest tier node by default
          setSelectedNode(sortedNodes[0]);
        } else {
          setActivatedNodes([]);
          setSelectedNode(null);
        }
      } catch (signError) {
        console.warn('First attempt error:', signError);
        
        // If first attempt fails, try again with a forced new signature
        if (signError.message && signError.message.includes('retrying')) {
          try {
            const { signature, message } = await getValidSignature(
              wallet.address,
              networkId,
              wallet.type || 'metamask',
              true // Force refresh signature
            );
            
            // Use the node-applications/wallet_applications/ endpoint to get nodes
            const response = await fetch('https://api.aeronyx.network/api/node-applications/wallet_applications/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                wallet_address: wallet.address,
                blockchain_network_id: networkId,
                signature: signature,
                message: message,
                wallet_type: wallet.type
              }),
            });
            
            if (!response.ok) {
              throw new Error(`Failed to fetch nodes: ${response.status} ${response.statusText}`);
            }
            
            const result = await response.json();
            
            if (!result.success) {
              throw new Error(result.message || 'Failed to fetch user nodes');
            }
            
            // Process the data as before
            const nodes = result.data && result.data.applications ? result.data.applications : [];
            
            if (nodes.length > 0) {
              const sortedNodes = [...nodes].sort((a, b) => {
                const tierA = a.tier_level || getTierLevel(a.tier_name || a.tier);
                const tierB = b.tier_level || getTierLevel(b.tier_name || b.tier);
                return tierB - tierA;
              });
              
              setActivatedNodes(sortedNodes);
              setSelectedNode(sortedNodes[0]);
            } else {
              setActivatedNodes([]);
              setSelectedNode(null);
            }
          } catch (retryError) {
            console.error('Retry failed:', retryError);
            throw retryError;
          }
        } else {
          // If it's not a signature error, rethrow
          throw signError;
        }
      }
    } catch (err) {
      console.error('Error fetching user nodes:', err);
      setError(`Failed to load your nodes: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to convert tier name to numeric level
  const getTierLevel = (tierName) => {
    if (!tierName) return 1;
    const lowerName = tierName.toLowerCase();
    if (lowerName.includes('apex')) return 4;
    if (lowerName.includes('master')) return 3;
    if (lowerName.includes('super')) return 2;
    return 1; // Standard/default
  };

  // Handle node selection change
  const handleNodeChange = (node) => {
    console.log("handleNodeChange called with node:", node.id, node.reference_code);
    
    // Ensure we're changing to a different node
    if (selectedNode?.id !== node.id) {
      console.log("Changing node from", selectedNode?.id, "to", node.id);
      
      // Clear signature cache before changing the selected node
      clearSignatureCache();
      console.log("Signature cache cleared");
      
      // Update the selected node state
      setSelectedNode(node);
      console.log("Selected node state updated");
    } else {
      console.log("Same node selected, no change needed");
    }
  };

  // Update network stats when received from child components
  const handleNetworkStatsUpdate = (stats) => {
    setNetworkStats(stats);
  };

  // Determine if current path is dashboard root
  const isDashboardRoot = location.pathname === '/dashboard';

  // Create context object with common props for child components
  const dashboardContext = {
    wallet,
    activatedNodes,
    selectedNode,
    isLoading,
    error,
    networkStats,
    formatWalletAddress,
    formatDate,
    formatNumber,
    onNodeChange: handleNodeChange,
    onNetworkStatsUpdate: handleNetworkStatsUpdate,
    refreshNodes: fetchUserNodes
  };

  return (
    <div className="dashboard-container">
      <DashboardSidebar 
        selectedNode={selectedNode}
        activatedNodes={activatedNodes}
        networkStats={networkStats}
        wallet={wallet}
      />
      
      <div className="dashboard-main">
        <DashboardHeader 
          selectedNode={selectedNode}
          activatedNodes={activatedNodes}
          onNodeChange={handleNodeChange}
          wallet={wallet}
        />
        
        <div className="dashboard-content">
          {isLoading ? (
            <div className="dashboard-loading">
              <div className="spinner"></div>
              <p>Loading your node data...</p>
            </div>
          ) : error ? (
            <div className="dashboard-error">
              <p>{error}</p>
              <button className="btn btn-primary" onClick={fetchUserNodes}>
                Try Again
              </button>
            </div>
          ) : (
            <Routes>
              <Route path="/" element={<DashboardOverview {...dashboardContext} />} />
              <Route path="nodes" element={<NodeManagement {...dashboardContext} />} />
              <Route path="invitations" element={<InvitationManagement {...dashboardContext} />} />
              <Route path="network" element={<NetworkManagement {...dashboardContext} />} />
              <Route path="*" element={<Navigate to="/" replace />} /> 
            </Routes>
          )}
        </div>
      </div>
      
      {/* Wallet Required Modal */}
      <WalletRequiredModal 
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
        onConnect={openWalletModal}
      />
    </div>
  );
};

export default Dashboard;
